import React, { useState, useEffect } from "react";
import axios from "../api/axios";
import qrImage from "../assets/qr.jpeg";
import {
  FiUploadCloud, FiCheckCircle, FiClock,
  FiRefreshCw, FiAlertCircle, FiX, FiImage, FiZap,
  FiArrowDownLeft, FiArrowUpRight
} from "react-icons/fi";

/* ═══════════════════════════════════════════
   VIBRANT NEUROMORPHISM STYLES — Consistent with All Dashboards
   Blue · Green · Purple · Amber palette
═══════════════════════════════════════════ */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .mp-root {
    font-family: 'Montserrat', sans-serif;
    background: linear-gradient(135deg, #e0e5ec 0%, #d1d6df 100%);
    min-height: 100%;
    padding: 6px 5px;
  }
  .mp-root *, .mp-root *::before, .mp-root *::after { box-sizing: border-box; }
  
  .mp-root ::-webkit-scrollbar { width: 6px; height: 6px; }
  .mp-root ::-webkit-scrollbar-track { 
    background: #d1d6df;
    border-radius: 99px;
    box-shadow: inset 0 1px 3px rgba(0,0,0,.1);
  }
  .mp-root ::-webkit-scrollbar-thumb { 
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    border-radius: 99px;
    box-shadow: inset 0 1px 2px rgba(255,255,255,.3), inset 0 -1px 2px rgba(0,0,0,.1);
  }

  .mp-page {
    max-width: 1280px;
    margin: 0 auto;
    display: flex; flex-direction: column; gap: 28px;
  }
  @media (max-width: 600px) { .mp-page { gap: 20px; } }

  /* ══════════════════════════════
     HERO SECTION — Vibrant Gradient
  ══════════════════════════════ */
  .mp-hero {
    background: linear-gradient(135deg, #0a1628 0%, #1e3a5f 50%, #2563eb 100%);
    border-radius: 9px;
    padding: 32px 36px;
    position: relative;
    overflow: hidden;
    box-shadow: 12px 12px 24px #8f95a0, -12px -12px 24px #ffffff;
    border: 1px solid rgba(255,255,255,0.2);
  }
  .mp-hero::before {
    content: '';
    position: absolute;
    top: -20%;
    right: -5%;
    width: 300px;
    height: 300px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(59,130,246,.2) 0%, transparent 70%);
    pointer-events: none;
  }
  .mp-hero::after {
    content: '';
    position: absolute;
    bottom: -20%;
    left: -5%;
    width: 250px;
    height: 250px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(139,92,246,.15) 0%, transparent 70%);
    pointer-events: none;
  }
  .mp-hero-badge {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(255,255,255,0.12);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.25);
    border-radius: 99px; padding: 6px 16px;
    font-size: 11px; font-weight: 800; color: #fbbf24;
    letter-spacing: .1em; text-transform: uppercase; margin-bottom: 14px;
    position: relative; z-index: 1;
  }
  .mp-badge-dot {
    width: 8px; height: 8px; border-radius: 50%; background: #fbbf24;
    animation: mp-pulse 2s infinite;
    box-shadow: 0 0 8px rgba(251,191,36,0.6);
  }
  @keyframes mp-pulse { 0%,100%{opacity:1; transform:scale(1)} 50%{opacity:.5; transform:scale(1.2)} }
  .mp-hero-title {
    font-size: clamp(24px, 5vw, 34px); font-weight: 900;
    color: #ffffff; line-height: 1.2; margin-bottom: 8px;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
    position: relative; z-index: 1;
  }
  .mp-hero-sub {
    font-size: 14px; font-weight: 600; color: rgba(255,255,255,0.85);
    position: relative; z-index: 1;
  }

  /* ══════════════════════════════
     MAIN TWO-COL LAYOUT
  ══════════════════════════════ */
  .mp-two-col {
    display: grid; grid-template-columns: 1fr 1fr; gap: 24px;
  }
  @media (max-width: 820px) { .mp-two-col { grid-template-columns: 1fr; } }

  /* ══════════════════════════════
     SHARED CARD — Neumorphism
  ══════════════════════════════ */
  .mp-card {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 9px;
    padding: 26px;
    box-shadow: 14px 14px 28px #8f95a0, -8px -8px 16px #ffffff;
  }
  .mp-card-title {
    font-size: 14px; font-weight: 900;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase; letter-spacing: .1em; margin-bottom: 24px;
    display: flex; align-items: center; gap: 10px;
  }
  .mp-card-title-bar {
    width: 6px; height: 22px; border-radius: 99px;
    background: linear-gradient(180deg, #fbbf24, #f59e0b);
    box-shadow: 0 2px 8px rgba(245,158,11,0.4);
    flex-shrink: 0;
  }

  /* ══════════════════════════════
     QR SECTION
  ══════════════════════════════ */
  .mp-qr-wrap {
    display: flex; flex-direction: column; align-items: center; gap: 20px;
  }
  .mp-qr-frame {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 24px;
    padding: 16px;
    box-shadow: 10px 10px 20px #8f95a0, -6px -6px 12px #ffffff;
    display: inline-block;
    transition: all 0.3s ease;
    position: relative;
  }
  .mp-qr-frame:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 14px 14px 24px #8f95a0, -8px -8px 16px #ffffff;
  }
  .mp-qr-img { width: 200px; height: 200px; object-fit: contain; border-radius: 16px; display: block; }
  @media (max-width: 480px) { .mp-qr-img { width: 180px; height: 180px; } }

  .mp-download-badge {
    position: absolute;
    bottom: 10px;
    right: 10px;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    color: #fbbf24;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 6px;
    box-shadow: 4px 4px 8px #8f95a0, -2px -2px 4px #ffffff;
  }

  .mp-qr-steps {
    width: 100%; display: flex; flex-direction: column; gap: 12px;
  }
  .mp-qr-step {
    display: flex; align-items: center; gap: 14px;
    padding: 12px 16px;
    border-radius: 18px;
    background: linear-gradient(145deg, #f0f4f9, #e8edf5);
    box-shadow: inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.6);
    transition: all 0.2s;
  }
  .mp-qr-step:hover {
    transform: translateX(4px);
  }
  .mp-qr-step-num {
    width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 900; color: #fbbf24;
    box-shadow: 3px 3px 6px #8f95a0, -2px -2px 4px #ffffff;
  }
  .mp-qr-step-text { font-size: 13px; font-weight: 700; color: #334155; }

  /* ══════════════════════════════
     FORM
  ══════════════════════════════ */
  .mp-form { display: flex; flex-direction: column; gap: 20px; }

  .mp-field-label {
    display: block; font-size: 12px; font-weight: 800;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-transform: uppercase; letter-spacing: .08em; margin-bottom: 8px;
  }
  .mp-required { color: #ef4444; margin-left: 2px; background: none; -webkit-text-fill-color: #ef4444; }
  .mp-optional { font-size: 10px; font-weight: 600; color: #94a3b8; text-transform: none; letter-spacing: 0; margin-left: 4px; background: none; -webkit-text-fill-color: #94a3b8; }

  .mp-input {
    width: 100%; padding: 14px 18px;
    border: none; border-radius: 20px;
    font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 600;
    color: #1e293b; background: linear-gradient(145deg, #e8edf5, #dce1e8);
    box-shadow: inset 5px 5px 12px rgba(0,0,0,0.08), inset -5px -5px 12px rgba(255,255,255,0.7);
    outline: none;
    transition: all 0.2s;
  }
  .mp-input:focus {
    box-shadow: inset 3px 3px 8px rgba(0,0,0,0.1), inset -3px -3px 8px rgba(255,255,255,0.8);
    border: 1px solid #2563eb;
  }
  .mp-input::placeholder { color: #94a3b8; font-weight: 500; }

  /* Amount wrap with ₹ prefix */
  .mp-amount-wrap { position: relative; }
  .mp-amount-prefix {
    position: absolute; left: 16px; top: 50%; transform: translateY(-50%);
    font-size: 18px; font-weight: 900;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    pointer-events: none;
  }
  .mp-amount-wrap .mp-input { padding-left: 40px; }

  /* Upload area */
  .mp-upload-label {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    width: 100%; min-height: 140px;
    border-radius: 20px;
    cursor: pointer;
    background: linear-gradient(145deg, #f0f4f9, #e8edf5);
    box-shadow: inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.6);
    transition: all 0.2s;
    position: relative; overflow: hidden;
  }
  .mp-upload-label:hover {
    background: linear-gradient(145deg, #e8edf5, #f0f4f9);
    box-shadow: inset 3px 3px 8px rgba(0,0,0,0.08), inset -3px -3px 8px rgba(255,255,255,0.7);
  }
  .mp-upload-label.has-file {
    box-shadow: 0 0 0 2px #10b981, inset 2px 2px 5px rgba(0,0,0,0.05), inset -2px -2px 5px rgba(255,255,255,0.6);
  }

  .mp-upload-placeholder {
    display: flex; flex-direction: column; align-items: center; gap: 8px;
    padding: 24px;
  }
  .mp-upload-icon { 
    color: #64748b;
    transition: color .2s;
  }
  .mp-upload-label:hover .mp-upload-icon { color: #f59e0b; }
  .mp-upload-text { font-size: 13px; font-weight: 800; color: #475569; }
  .mp-upload-hint { font-size: 11px; font-weight: 600; color: #94a3b8; }

  .mp-upload-preview { width: 100%; height: 130px; object-fit: contain; border-radius: 14px; padding: 8px; }
  .mp-upload-remove {
    position: absolute; top: 10px; right: 10px;
    width: 32px; height: 32px; border-radius: 50%;
    background: linear-gradient(135deg, #ef4444, #dc2626);
    border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: #fff;
    transition: all .2s;
    box-shadow: 3px 3px 6px rgba(0,0,0,0.2);
  }
  .mp-upload-remove:hover { transform: scale(1.05); background: linear-gradient(135deg, #dc2626, #b91c1c); }
  .mp-filename { font-size: 11px; font-weight: 600; color: #64748b; margin-top: 6px; text-align: center; }

  /* Alerts */
  .mp-alert {
    display: flex; align-items: flex-start; gap: 12px;
    padding: 14px 18px;
    border-radius: 20px;
    font-size: 13px; font-weight: 700;
  }
  .mp-alert.success { 
    background: linear-gradient(145deg, #ecfdf5, #d1fae5);
    border: 1px solid #bbf7d0; 
    color: #059669; 
  }
  .mp-alert.error { 
    background: linear-gradient(145deg, #fef2f2, #fee2e2);
    border: 1px solid #fecaca; 
    color: #b91c1c; 
  }

  /* Submit button */
  .mp-submit-btn {
    width: 100%; padding: 16px;
    border: none; border-radius: 60px; cursor: pointer;
    font-family: 'Montserrat', sans-serif; font-size: 14px; font-weight: 900;
    display: flex; align-items: center; justify-content: center; gap: 10px;
    transition: all .22s;
    letter-spacing: .05em;
  }
  .mp-submit-btn.ready {
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    color: #fbbf24;
    box-shadow: 10px 10px 20px #8f95a0, -5px -5px 10px #ffffff;
  }
  .mp-submit-btn.ready:hover { transform: translateY(-3px); box-shadow: 14px 14px 24px #8f95a0, -7px -7px 14px #ffffff; }
  .mp-submit-btn.loading { 
    background: linear-gradient(145deg, #e2e8f0, #cbd5e1); 
    color: #94a3b8; 
    cursor: not-allowed; 
    box-shadow: inset 2px 2px 5px rgba(0,0,0,0.1), inset -2px -2px 5px rgba(255,255,255,0.5);
  }

  /* upload spinner */
  @keyframes mp-spin { to { transform: rotate(360deg); } }
  .mp-spin { animation: mp-spin .7s linear infinite; }

  /* ══════════════════════════════
     HISTORY SECTION SHELL
  ══════════════════════════════ */
  .mp-history-card {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 9px;
    overflow: hidden;
    box-shadow: 14px 14px 28px #8f95a0, -8px -8px 16px #ffffff;
  }
  .mp-history-header {
    padding: 20px 24px;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
  }
  .mp-history-title {
    font-size: 14px; font-weight: 900; color: #ffffff;
    text-transform: uppercase; letter-spacing: .1em;
    display: flex; align-items: center; gap: 10px;
  }
  .mp-history-title svg { color: #fbbf24; }
  .mp-refresh-btn {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 8px 16px;
    border-radius: 40px;
    border: 1px solid rgba(255,255,255,0.2);
    background: rgba(255,255,255,0.1);
    cursor: pointer;
    font-family: 'Montserrat', sans-serif;
    font-size: 12px; font-weight: 800;
    color: rgba(255,255,255,0.8);
    transition: all .2s;
    backdrop-filter: blur(4px);
  }
  .mp-refresh-btn:hover { background: rgba(255,255,255,0.2); color: #fff; transform: translateY(-1px); }

  .mp-table-empty {
    padding: 60px 24px; text-align: center;
    font-size: 14px; font-weight: 700; color: #94a3b8;
    background: linear-gradient(145deg, #f0f4f9, #e8edf5);
  }
  .mp-table-empty-icon { font-size: 48px; margin-bottom: 12px; opacity: 0.6; }

  /* ══════════════════════════════
     TRANSACTION CARDS — Unified Credit/Debit Design
  ══════════════════════════════ */
  .txc-grid {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 16px;
  }
  @media (max-width: 480px) {
    .txc-grid { padding: 12px; gap: 8px; }
  }

  .txc-card {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: linear-gradient(145deg, #eef2f8, #dfe4ec);
    border-radius: 12px;
    padding: 12px 16px;
    box-shadow: 4px 4px 10px #9aa0ab, -3px -3px 8px #ffffff;
  }

  .txc-card-header {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .txc-icon {
    width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 2px 2px 6px rgba(0,0,0,0.1), -1px -1px 4px rgba(255,255,255,0.7);
  }
  .txc-icon.credit   { background: linear-gradient(135deg, #d1fae5, #a7f3d0); color: #059669; }
  .txc-icon.debit    { background: linear-gradient(135deg, #fee2e2, #fecaca); color: #dc2626; }
  .txc-icon.pending  { background: linear-gradient(135deg, #fef3c7, #fde68a); color: #d97706; }
  .txc-icon.rejected { background: linear-gradient(135deg, #fee2e2, #fecaca); color: #dc2626; }

  .txc-title-wrap { flex: 1; min-width: 0; }
  .txc-type { font-size: 12px; font-weight: 800; color: #1e293b; line-height: 1.2; }
  .txc-datetime { font-size: 10px; font-weight: 700; color: #94a3b8; margin-top: 2px; }

  .txc-amount-wrap { text-align: right; flex-shrink: 0; display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
  .txc-amount { font-size: 14px; font-weight: 900; white-space: nowrap; }
  .txc-amount.credit   { color: #059669; }
  .txc-amount.debit    { color: #dc2626; }
  .txc-amount.pending  { color: #d97706; }
  .txc-amount.rejected { color: #dc2626; text-decoration: line-through; text-decoration-color: rgba(220,38,38,0.45); }
  .txc-balance { font-size: 10px; font-weight: 700; color: #94a3b8; white-space: nowrap; }

  /* Transaction Details Section */
  .txc-details {
    background: linear-gradient(145deg, #f8fafc, #f1f5f9);
    border-radius: 10px;
    padding: 10px 14px;
    margin-top: 8px;
    box-shadow: 2px 2px 8px rgba(0,0,0,0.06), -1px -1px 6px rgba(255,255,255,0.7);
    border: 1px solid rgba(226,232,240,0.6);
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .txc-detail-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    font-size: 10px;
  }

  .txc-detail-label {
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .txc-detail-value {
    font-weight: 600;
    color: #1e293b;
    text-align: right;
  }

  .txc-screenshot-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    color: #ffffff;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 700;
    text-decoration: none;
    box-shadow: 0 2px 4px rgba(37,99,235,0.2);
    align-self: flex-start;
  }

  .txc-rejection-message {
    background: linear-gradient(145deg, #fef2f2, #fee2e2);
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 8px 10px;
    margin-top: 6px;
  }

  .txc-rejection-label {
    font-size: 9px;
    font-weight: 800;
    color: #dc2626;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 4px;
  }

  .txc-rejection-text {
    font-size: 10px;
    font-weight: 600;
    color: #991b1b;
    line-height: 1.3;
  }

  .txc-balance-progression {
    background: linear-gradient(145deg, #f0fdf4, #dcfce7);
    border: 1px solid #bbf7d0;
    border-radius: 8px;
    padding: 8px 10px;
    margin-top: 6px;
  }

  .txc-bp-label {
    font-size: 9px;
    font-weight: 800;
    color: #059669;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 6px;
  }

  .txc-bp-values {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    font-size: 10px;
    font-weight: 700;
    color: #334155;
  }

  .txc-bp-arrow {
    color: #059669;
    font-weight: 900;
  }

  .txc-deduction {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    font-size: 9px;
    font-weight: 700;
    color: #64748b;
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid #bbf7d0;
  }

  /* ══════════════════════════════
     SKELETON (initial page load)
  ══════════════════════════════ */
  .mp-skeleton {
    display: flex; flex-direction: column; gap: 24px; padding: 20px;
    max-width: 1280px; margin: 0 auto;
  }
  .mp-skel {
    border-radius: 32px;
    background: #dce1e8;
    position: relative;
    overflow: hidden;
    box-shadow: 8px 8px 16px #8f95a0, -8px -8px 16px #ffffff;
  }
  .mp-skel::after {
    content:''; position:absolute; inset:0;
    background: linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.5) 50%, transparent 75%);
    background-size:200% 100%; animation:mp-shimmer 1.4s infinite;
  }
  @keyframes mp-shimmer { from{background-position:-200% 0} to{background-position:200% 0} }
  .mp-skel-hero { height: 140px; }
  .mp-skel-row  { display:grid; grid-template-columns:1fr 1fr; gap:24px; }
  @media (max-width:820px){ .mp-skel-row{ grid-template-columns:1fr; } }
  .mp-skel-box  { height: 400px; border-radius: 28px; background: #dce1e8; position: relative; overflow: hidden; }
  .mp-skel-box::after {
    content:''; position:absolute; inset:0;
    background: linear-gradient(90deg, transparent 25%, rgba(255,255,255,0.5) 50%, transparent 75%);
    background-size:200% 100%; animation:mp-shimmer 1.4s infinite;
  }
  .mp-skel-card { height: 220px; }
`;

/* ═══════════════════════════════════════════
   CLOUDINARY CONFIG
═══════════════════════════════════════════ */
const CLOUDINARY_URL    = `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`;
const CLOUDINARY_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

/* ═══════════════════════════════════════════
   PRESENTATION HELPERS (no business logic here)
═══════════════════════════════════════════ */
const formatDateTime = (iso) => {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }),
    time: d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }),
  };
};

const formatINR = (n) => `₹${Math.floor(Math.abs(n) || 0).toLocaleString("en-IN")}`;

/* Turns each payment record (which the API already returns) into one or
   multiple transaction entries — a credit for the amount received, and
   (when it happened) matching debits for each share-purchase deduction.
   Purely a display-layer reshape; the underlying history data is untouched.
   Matches share purchases to payments by timestamp to show separate loan transactions. */
const buildTransactions = (history, sharePurchases) => {
  const entries = [];
  history.forEach((p) => {
    // For pending/rejected, show balanceBefore (current balance before transaction)
    // For approved, show balanceAfter (balance after transaction)
    const balanceToShow = p.status === "approved" ? p.balanceAfter : p.balanceBefore;
    
    entries.push({
      id: `${p._id}-credit`,
      type: "credit",
      status: p.status,
      amount: p.amount,
      date: p.createdAt,
      balance: balanceToShow,
      upiId: p.upiId,
      transactionId: p.transactionId,
      screenshotUrl: p.screenshotUrl,
      adminNote: p.adminNote,
      walletType: p.walletType,
      balanceBefore: p.balanceBefore,
      balanceAfter: p.balanceAfter,
      sharePurchaseDeduction: p.sharePurchaseDeduction,
    });
    
    // For approved payments, match share purchases by timestamp and create separate debit entries
    if (p.status === "approved" && p.sharePurchaseDeduction > 0) {
      // Find share purchases that happened after this payment was reviewed
      const paymentReviewTime = new Date(p.reviewedAt || p.createdAt);
      const matchingPurchases = sharePurchases.filter(sp => {
        const purchaseTime = new Date(sp.createdAt);
        // Match if purchase happened within 24 hours after payment review
        const timeDiff = purchaseTime - paymentReviewTime;
        return timeDiff >= 0 && timeDiff <= 24 * 60 * 60 * 1000;
      });
      
      // If we found matching purchases, create separate debit entries
      if (matchingPurchases.length > 0) {
        matchingPurchases.forEach((sp, index) => {
          entries.push({
            id: `${p._id}-debit-${index}`,
            type: "debit",
            status: "approved",
            amount: sp.amount,
            date: sp.createdAt,
            balance: (p.balanceAfter || 0) - sp.amount,
            sharePurchaseId: sp._id,
            planName: sp.planName,
          });
        });
      } else {
        // Fallback: show combined deduction if no matches found
        entries.push({
          id: `${p._id}-debit`,
          type: "debit",
          status: "approved",
          amount: p.sharePurchaseDeduction,
          date: p.reviewedAt || p.createdAt,
          balance: (p.balanceAfter || 0) - p.sharePurchaseDeduction,
        });
      }
    }
  });
  return entries.sort((a, b) => new Date(b.date) - new Date(a.date));
};

/* Single transaction row/card — same look for every credit or debit entry. */
const TransactionCard = ({ t }) => {
  const tone = t.status === "rejected" ? "rejected" : t.status === "pending" ? "pending" : t.type;
  const isCredit = t.type === "credit";
  const { date, time } = formatDateTime(t.date);

  return (
    <div className="txc-card">
      <div className="txc-card-header">
        <div className={`txc-icon ${tone}`}>
          {isCredit ? <FiArrowDownLeft size={14} /> : <FiArrowUpRight size={14} />}
        </div>
        <div className="txc-title-wrap">
          <div className="txc-type">
            {isCredit ? "Wallet Credit" : "Loan Pay"}
            {t.status === "pending" ? " · Pending" : t.status === "rejected" ? " · Rejected" : ""}
          </div>
          <div className="txc-datetime">{date} · {time}</div>
        </div>
        <div className="txc-amount-wrap">
          <div className={`txc-amount ${tone}`}>
            {tone === "debit" || tone === "rejected" ? "−" : "+"}{formatINR(t.amount)}
          </div>
          {t.balance !== undefined && (
            <div className="txc-balance">Bal: {formatINR(t.balance)}</div>
          )}
        </div>
      </div>
      
      {/* Show full details for credit transactions (pending/approved/rejected) */}
      {isCredit && (
        <div className="txc-details">
          {/* Transaction ID */}
          {t.transactionId && (
            <div className="txc-detail-item">
              <span className="txc-detail-label">Transaction ID:</span>
              <span className="txc-detail-value">{t.transactionId}</span>
            </div>
          )}
          
          {/* UPI ID */}
          {t.upiId && (
            <div className="txc-detail-item">
              <span className="txc-detail-label">UPI ID:</span>
              <span className="txc-detail-value">{t.upiId}</span>
            </div>
          )}
          
          {/* Wallet Type */}
          {t.walletType && (
            <div className="txc-detail-item">
              <span className="txc-detail-label">Wallet:</span>
              <span className="txc-detail-value">{t.walletType === 'lbbp' ? 'LBBP Wallet' : 'PU&AP Wallet'}</span>
            </div>
          )}
          
          {/* Screenshot Link - Show for all statuses */}
          {t.screenshotUrl && (
            <a 
              href={t.screenshotUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="txc-screenshot-link"
            >
              <FiImage size={10} /> View Screenshot
            </a>
          )}
          
          {/* Admin Rejection Message - Show only for rejected payments */}
          {t.status === "rejected" && t.adminNote && (
            <div className="txc-rejection-message">
              <div className="txc-rejection-label">Admin Message:</div>
              <div className="txc-rejection-text">{t.adminNote}</div>
            </div>
          )}
          
          {/* Balance Progression - Show for approved payments */}
          {t.status === "approved" && t.balanceBefore !== undefined && t.balanceAfter !== undefined && (
            <div className="txc-balance-progression">
              <div className="txc-bp-label">Balance Progression:</div>
              <div className="txc-bp-values">
                <span>Before: {formatINR(t.balanceBefore)}</span>
                <span className="txc-bp-arrow">→</span>
                <span>After: {formatINR(t.balanceAfter)}</span>
              </div>
              {t.sharePurchaseDeduction > 0 && (
                <div className="txc-deduction">
                  <span>Share Purchase Deduction: -{formatINR(t.sharePurchaseDeduction)}</span>
                  <span className="txc-bp-arrow">→</span>
                  <span>Remaining: {formatINR(t.balanceAfter - t.sharePurchaseDeduction)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const ManualPayment = () => {
  const [form, setForm] = useState({ amount: "", upiId: "", transactionId: "" });
  const [screenshotFile, setScreenshotFile]       = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [uploading, setUploading]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg]     = useState("");
  const [history, setHistory]       = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [sharePurchases, setSharePurchases] = useState([]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const [historyRes, purchasesRes] = await Promise.all([
        axios.get("/payment/my-history"),
        axios.get("/share/my-purchases")
      ]);
      setHistory(historyRes.data || []);
      setSharePurchases(purchasesRes.data?.purchases || []);
    } catch (err) {
      console.error("Failed to fetch payment history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchWalletBalance = async () => {
    try {
      const res = await axios.get("/share/dashboard-summary");
      setWalletBalance(res.data?.lbbpWallet || 0);
    } catch (err) {
      console.error("Failed to fetch wallet balance:", err);
    }
  };

  useEffect(() => { 
    fetchHistory(); 
    fetchWalletBalance();
  }, []);

  const handleChange = (e) =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    if (!allowed.includes(file.type)) { setErrorMsg("Only JPG, PNG, WEBP images allowed."); return; }
    if (file.size > 5 * 1024 * 1024) { setErrorMsg("File size must be under 5MB."); return; }
    setScreenshotFile(file);
    setScreenshotPreview(URL.createObjectURL(file));
    setErrorMsg("");
  };

  const removeFile = (e) => {
    e.preventDefault(); e.stopPropagation();
    setScreenshotFile(null); setScreenshotPreview(null);
  };

  const handleDownloadQR = () => {
    const link = document.createElement('a');
    link.href = qrImage;
    link.download = 'ABM_UPI_QR_Code.jpg';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const uploadToCloudinary = async (file) => {
    const data = new FormData();
    data.append("file", file);
    data.append("upload_preset", CLOUDINARY_PRESET);
    const res = await fetch(CLOUDINARY_URL, { method: "POST", body: data });
    if (!res.ok) throw new Error("Cloudinary upload failed");
    return (await res.json()).secure_url;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg(""); setSuccessMsg("");
    if (!form.amount || !form.upiId) { setErrorMsg("Amount and UPI ID are required."); return; }
    if (!screenshotFile) { setErrorMsg("Please upload a payment screenshot."); return; }
    try {
      setUploading(true);
      const screenshotUrl = await uploadToCloudinary(screenshotFile);
      setUploading(false); setSubmitting(true);
      await axios.post("/payment/submit", {
        amount: Number(form.amount),
        upiId: form.upiId,
        transactionId: form.transactionId || undefined,
        screenshotUrl,
        walletType: "lbbp",
      });
      setSuccessMsg("Payment submitted! Admin will review and confirm shortly.");
      setForm({ amount: "", upiId: "", transactionId: "" });
      setScreenshotFile(null); setScreenshotPreview(null);
      fetchHistory();
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || "Failed to submit payment. Please try again.");
    } finally {
      setUploading(false); setSubmitting(false);
    }
  };

  const isLoading = uploading || submitting;

  if (historyLoading && history.length === 0) return (
    <div className="mp-root">
      <style>{styles}</style>
      <div className="mp-skeleton">
        <div className="mp-skel mp-skel-hero" />
        <div className="mp-skel-row">
          <div className="mp-skel-box" />
          <div className="mp-skel-box" />
        </div>
        <div className="mp-skel mp-skel-card" />
      </div>
    </div>
  );

  return (
    <div className="mp-root">
      <style>{styles}</style>
      <div className="mp-page">

        {/* ── Hero ── */}
        <div className="mp-hero">
          <div className="mp-hero-badge"><div className="mp-badge-dot"/>UPI Payment</div>
          <div className="mp-hero-title">Send Money to ABM</div>
          <div className="mp-hero-sub">
            Scan the QR code below and upload your payment screenshot for admin confirmation.
          </div>
        </div>

        {/* ── Wallet Balance Card ── */}
        <div className="mp-card">
          <div className="mp-card-title"><div className="mp-card-title-bar"/>Add Money Wallet</div>
          <div style={{ textAlign: 'center', padding: '' }}>
            <div style={{ fontSize: '24px', fontWeight: '800', background: 'linear-gradient(135deg, #2563eb, #8b5cf6)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              ₹{Math.floor(walletBalance).toLocaleString("en-IN")}
            </div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', marginTop: '8px' }}>
              Current Balance
            </div>
          </div>
        </div>

        {/* ── QR + Form ── */}
        <div className="mp-two-col">

          {/* QR Card */}
          <div className="mp-card">
            <div className="mp-card-title"><div className="mp-card-title-bar"/>Scan & Pay via UPI</div>
            <div className="mp-qr-wrap">
              <div className="mp-qr-frame" onClick={handleDownloadQR} style={{ cursor: 'pointer' }}>
                <img src={qrImage} alt="Admin UPI QR Code" className="mp-qr-img" />
                <div className="mp-download-badge">
                  <FiUploadCloud size={12} /> Download
                </div>
              </div>
              <div className="mp-qr-steps">
                {[
                  "Open PhonePe, GPay, Paytm or any UPI app",
                  "Scan this QR code and enter the amount",
                  "Complete the payment and save the screenshot",
                  "Fill in the form and upload the screenshot",
                ].map((step, i) => (
                  <div className="mp-qr-step" key={i}>
                    <div className="mp-qr-step-num">{i + 1}</div>
                    <div className="mp-qr-step-text">{step}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Form Card */}
          <div className="mp-card">
            <div className="mp-card-title"><div className="mp-card-title-bar"/>Payment Details</div>

            {successMsg && (
              <div className="mp-alert success" style={{ marginBottom: 20 }}>
                <FiCheckCircle size={16}/> {successMsg}
              </div>
            )}
            {errorMsg && (
              <div className="mp-alert error" style={{ marginBottom: 20 }}>
                <FiAlertCircle size={16}/> {errorMsg}
              </div>
            )}

            <form className="mp-form" onSubmit={handleSubmit}>

              {/* Amount */}
              <div>
                <label className="mp-field-label">
                  Amount Paid <span className="mp-required">*</span>
                </label>
                <div className="mp-amount-wrap">
                  <span className="mp-amount-prefix">₹</span>
                  <input
                    type="number" name="amount" className="mp-input"
                    value={form.amount} onChange={handleChange}
                    min="1" placeholder="Enter amount paid" required
                  />
                </div>
              </div>

              {/* UPI ID */}
              <div>
                <label className="mp-field-label">
                  Your UPI ID <span className="mp-required">*</span>
                </label>
                <input
                  type="text" name="upiId" className="mp-input"
                  value={form.upiId} onChange={handleChange}
                  placeholder="e.g. yourname@upi" required
                />
              </div>

              {/* Transaction ID */}
              <div>
                <label className="mp-field-label">
                  Transaction ID <span className="mp-optional">(optional)</span>
                </label>
                <input
                  type="text" name="transactionId" className="mp-input"
                  value={form.transactionId} onChange={handleChange}
                  placeholder="UPI reference number (if available)"
                />
              </div>

              {/* Screenshot upload */}
              <div>
                <label className="mp-field-label">
                  Payment Screenshot <span className="mp-required">*</span>
                </label>
                <label className={`mp-upload-label ${screenshotPreview ? "has-file" : ""}`}>
                  {screenshotPreview ? (
                    <>
                      <img src={screenshotPreview} alt="Preview" className="mp-upload-preview" />
                      <button className="mp-upload-remove" onClick={removeFile} type="button">
                        <FiX size={14}/>
                      </button>
                    </>
                  ) : (
                    <div className="mp-upload-placeholder">
                      <FiUploadCloud size={34} className="mp-upload-icon" />
                      <span className="mp-upload-text">Click to upload screenshot</span>
                      <span className="mp-upload-hint">JPG, PNG, WEBP — max 5 MB</span>
                    </div>
                  )}
                  <input
                    type="file" accept="image/jpeg,image/png,image/jpg,image/webp"
                    onChange={handleFileChange} className="hidden" style={{ display: "none" }}
                  />
                </label>
                {screenshotFile && (
                  <div className="mp-filename">📎 {screenshotFile.name}</div>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className={`mp-submit-btn ${isLoading ? "loading" : "ready"}`}
              >
                {uploading
                  ? <><FiUploadCloud size={16} className="mp-spin"/> Uploading screenshot…</>
                  : submitting
                  ? <><FiZap size={16} className="mp-spin"/> Submitting…</>
                  : <><FiCheckCircle size={16}/> Submit Payment</>
                }
              </button>
            </form>
          </div>
        </div>

        {/* ── Payment History ── */}
        <div className="mp-history-card">
          <div className="mp-history-header">
            <div className="mp-history-title">
              <FiClock size={16}/> My Payment History
            </div>
            <button className="mp-refresh-btn" onClick={fetchHistory}>
              <FiRefreshCw size={14} className={historyLoading ? "mp-spin" : ""}/> Refresh
            </button>
          </div>

          {historyLoading ? (
            <div className="mp-table-empty">
              <div className="mp-table-empty-icon">⏳</div>
              Loading payment history…
            </div>
          ) : history.length === 0 ? (
            <div className="mp-table-empty">
              <div className="mp-table-empty-icon">📭</div>
              No payment submissions yet
            </div>
          ) : (
            <div className="txc-grid">
              {buildTransactions(history, sharePurchases).map(t => (
                <TransactionCard key={t.id} t={t} />
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default ManualPayment;